import type { TodoHotkeyConfig} from "@web/src/app/hooks/useHotkeys";
import { useHotkeys } from "@web/src/app/hooks/useHotkeys";
import { useEffect } from "react";
import { create } from "zustand";

export type HotkeyContext = {
  name: string;
  priority: number;
  /**
   * Names of other contexts that are allowed to also be active when this context
   * is active (top of the stack).
   */
  allowedContexts?: string[];
  /**
   * Names of other contexts that when active, will allow this context to also be active.
   */
  piggybackContexts?: string[];
  /**
   * If true, the context will not be pushed onto the stack. This makes these set of shortcuts
   * non-blocking, often used with piggybackContexts.
   */
  skipPushStack?: boolean;
};

// List of all contexts and priorities
export const TODO_ITEM_MENU_TRIGGER_CONTEXT: HotkeyContext = {
  name: "TODO_ITEM_MENU_TRIGGER_CONTEXT",
  priority: -1,
  piggybackContexts: ["SELECT_MODE_CTX", "TODO_DROPDOWN_MENU_CONTEXT"],
  skipPushStack: true,
};
export const SELECT_MODE_CTX = {
  name: "SELECT_MODE_CTX",
  priority: -1,
};
export const EDIT_MODE_CTX = { name: "EDIT_MODE_CTX", priority: -1 };
export const CONTEXT_MENU_CONTEXT = { name: "TODO_CONTEXT_MENU", priority: 0 };
export const AREA_SELECT_SHORTCUT_CTX = { name: "AREA_SELECTION_TOAST", priority: 0 };
export const GLOBAL_COMMAND_CTX = { name: "GLOBAL_COMMAND_CTX", priority: 1 };
export const SPACE_SELECTOR_CTX = { name: "SPACE_SELECTOR", priority: 1 };
export const EDIT_NOTES_CTX = { name: "EDIT_NOTES", priority: 2 };
export const CREATE_SPACE_MODAL_INPUT_CTX = { name: "CREATE_SPACE_MODAL_INPUT", priority: 2 };
export const CREATE_SPACE_MODAL_ICONS_CTX = { name: "CREATE_SPACE_MODAL_ICONS", priority: 2 };
export const CREATE_SPACE_MODAL_ACTIONS_CTX = { name: "CREATE_SPACE_MODAL_ACTIONS", priority: 2 };
export const HISTORY_CTX = { name: "HISTORY_CTX", priority: 3 };
export const TODO_HASHTAG_OPTIONS_CTX = { name: "TODO_HASHTAG_OPTIONS_CTX", priority: 3 };
export const TODO_CMD_PREVIEW_CTX = { name: "TODO_CMD_PREVIEW_CTX", priority: 3 };
export const TODO_FREE_CMD_PREVIEW_CTX = { name: "TODO_FREE_CMD_PREVIEW_CTX", priority: 3 };
export const DROPDOWN_MENU_CONTEXT = { name: "DROPDOWN_MENU_CONTEXT", priority: 4 };
export const TODO_DROPDOWN_MENU_CONTEXT = {
  name: "TODO_DROPDOWN_MENU_CONTEXT",
  priority: 4,
};
export const IMPERATIVE_MODAL_CTX = { name: "IMPERATIVE_MODAL", priority: 5 };
export const SHARE_SPACE_CTX = { name: "SHARE_SPACE_MODAL_CTX", priority: 5 };
/** Radix home modals (settings, calendar, etc.): blocks todo shortcuts while open. */
export const HOME_MODALS_CTX = { name: "HOME_MODALS", priority: 5 };
export const PAYWALL_CTX = { name: "PAYWALL_MODEL", priority: 6 };

export const ctxStack: HotkeyContext[] = [];

/**
 * We use a zustand store because we need to trigger a
 * react re-render, so that the useEffect within useHotkeys gets re-rendered
 * to update the latest state of active contexts
 */
export const useHotkeyContext = create<{
  allowedShortcuts?: string[];
  setAllowedShortcuts: (shortcuts: string[] | undefined) => void;
  currentContext: HotkeyContext | undefined;
  setCurrentContext: (name: HotkeyContext | undefined) => void;
}>((set) => ({
  allowedShortcuts: undefined,
  setAllowedShortcuts: (shortcuts) => set({ allowedShortcuts: shortcuts }),
  currentContext: undefined,
  setCurrentContext: (currentContext) => set({ currentContext }),
}));

export function useCtxHotkeys(configs: TodoHotkeyConfig[], ctx: HotkeyContext, isCtxActive: boolean) {
  const { currentContext, setCurrentContext, allowedShortcuts } = useHotkeyContext(
    ({ currentContext, setCurrentContext, allowedShortcuts }) => ({
      currentContext,
      setCurrentContext,
      allowedShortcuts,
    })
  );

  const isCurrentCtx = currentContext?.name === ctx.name;
  const canPiggybackCurrent = ctx.piggybackContexts?.some((ctxName) => currentContext?.name === ctxName);
  const isAllowedWithCurrent = !!currentContext?.allowedContexts?.includes(ctx.name);

  useHotkeys(configs, {
    isActive: isCtxActive && (isCurrentCtx || canPiggybackCurrent || isAllowedWithCurrent),
    activeFilter: allowedShortcuts,
  });

  useEffect(() => {
    function updateCurrentContext() {
      if (useHotkeyContext.getState().currentContext?.name !== ctxStack[ctxStack.length - 1]?.name) {
        // only set if it's not the same, otherwise we might end up in an
        // infinite loop of setstate triggers
        setCurrentContext(ctxStack[ctxStack.length - 1]);
      }
    }

    if (isCtxActive && !ctx.skipPushStack) {
      hookHandles.pushCtx(ctx);
    } else {
      hookHandles.releaseCtx(ctx);
    }

    updateCurrentContext();
    return () => {
      // release when unmount
      hookHandles.releaseCtx(ctx);
      updateCurrentContext();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCtxActive, hookHandles]); // we ignore ctx since it should be constant

  return hookHandles;
}

const hookHandles = {
  pushCtx: (ctx: HotkeyContext) => {
    const idx = ctxStack.findIndex((c) => c.name === ctx.name);
    if (idx !== -1) {
      ctxStack.splice(idx, 1);
    }
    ctxStack.push(ctx);
    ctxStack.sort((a, b) => a.priority - b.priority);
  },
  releaseCtx: (ctx: HotkeyContext) => {
    const idx = ctxStack.findIndex((c) => c.name === ctx.name);
    if (idx !== -1) {
      ctxStack.splice(idx, 1);
    }
    ctxStack.sort((a, b) => a.priority - b.priority);
  },
};

if (typeof window !== "undefined") {
  window.printShortcutContext = () => {
    console.log(ctxStack);
  };
}
